import { Pencil, Trash2 } from 'lucide-react';
import '../ActionMenu.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import GroupPostForm from './GroupPostForm';
import { useParams } from 'react-router-dom';
import GroupPostCard from './GroupPostcard';
import LikeButtons from '../icon/LikeButton'
import UnLikeButtons from '../icon/unlikebutton';
import Replybutton from '../icon/replybutton';
import InviteUser from './InviteUser';
import DeleteButtons from '../icon/deleteicon';
import Slider from 'react-slick';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Modal from 'react-modal';
import { AnimatePresence, color, motion } from 'framer-motion';
import { getApiBaseUrl } from '../../config/api';
import { getAuthorDisplayName } from '../../utils/displayName';
import { nestGroupReplies } from '../../utils/nestReplies';
import { resolveAvatarUrl, handleAvatarError } from '../../utils/profilePicUrl';
import { isMediaImage, isMediaVideo, isDisplayableMedia, getMediaMimeType } from '../../utils/mediaDetect';
import PostMediaGrid from '../shared/PostMediaGrid';
import GroupReplyItem from './GroupReplyItem';
import EditGroupPostForm from './EditGroupPostForm';
import HiringForm from '../HiringForm';
import LookingForJobForm from '../LookingForJobForm';
import PhotoLightbox from '../PhotoLightbox';
import PostCardHeader from '../shared/PostCardHeader';
import PostActionsSection from '../shared/PostActionsSection';
import WhoLikedModal from '../shared/WhoLikedModal';
import { getPostCardStyle, postContentStyle } from '../shared/postCardStyles';
import { DEFAULT_REACTION } from '../../utils/reactions';
import {
  canDeleteGroupPost,
  canDeleteGroupReply,
  canEditGroupPost,
} from '../../utils/groupRole';
import MentionInput from '../shared/MentionInput';
import CaptionAssist, { plainExcerpt } from '../shared/CaptionAssist';
import { appendMentionIds, createLinkifiedText, toStoredMentionText } from '../../utils/mentions';
import ArticleCard, { BLOG_CARD_PROPS } from '../shared/ArticleCard';
import HiringCard from '../shared/HiringCard';
import NewPostsBanner from '../shared/NewPostsBanner';
import useLiveRefresh from '../../hooks/useLiveRefresh';
import { mergeFeedPosts, isFeedNearTop } from '../../utils/mergeFeedPosts';
import LookingForJobCard from '../shared/LookingForJobCard';

const API_URL = getApiBaseUrl();
const authorDisplayName = getAuthorDisplayName;

const appendMentionedField = (formData, fieldName, text, extraIds) => {
  const stored = toStoredMentionText(text ?? '');
  formData.append(fieldName, stored);
  appendMentionIds(formData, stored, extraIds);
  return stored;
};

const ReplyForm = ({ onSubmit, userId, context = "" }) => {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim() && files.length === 0) return;
    onSubmit(text, files);
    setText("");
    setFiles([]);
    e.target.reset();
  };

  return (
    <form
      onSubmit={submit}
      style={{
        marginTop: 10,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <MentionInput
        userId={userId}
        value={text}
        onChange={setText}
        placeholder="Write a reply... Type @ to mention a connection"
        rows={3}
        style={{
          padding: 10,
          borderRadius: 5,
          border: "1px solid #ccc",
          minHeight: "80px",
          resize: "vertical",
        }}
      />
      <CaptionAssist
        kind="reply"
        text={text}
        context={plainExcerpt(context, 700)}
        userId={userId}
        onApply={setText}
      />

      {/* Drag and Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current.click()}
        style={{
          border: "2px dashed #aaa",
          padding: 20,
          textAlign: "center",
          borderRadius: 10,
          cursor: "pointer",
          backgroundColor: "#f9f9f9",
        }}
      >
        Drag & drop media files here, or click to select
        <input
          type="file"
          name="media[]"
          multiple
          accept="image/*,video/*"
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: "none" }}
        />
      </div>

      {/* File preview */}
      {files.length > 0 && (
        <div style={{ fontSize: 14 }}>
          <strong>Selected files:</strong>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              marginTop: "8px",
            }}
          >
            {files.map((file, index) => {
              const isImage = file.type.startsWith("image/");
              const isVideo = file.type.startsWith("video/");

              return (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "6px",
                    width: "100px",
                    height: "100px",
                    textAlign: "center",
                  }}
                >
                  {isImage && (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  )}

                  {isVideo && (
                    <video
                      src={URL.createObjectURL(file)}
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "4px",
                        objectFit: "cover",
                      }}
                      controls
                    />
                  )}

                  {!isImage && !isVideo && (
                    <div style={{ fontSize: "12px", padding: "20px 0" }}>
                      📄 {file.name}
                    </div>
                  )}

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => {
                      setFiles((prev) => prev.filter((_, i) => i !== index));
                    }}
                    style={{
                      position: "absolute",
                      top: "-5px",
                      right: "-5px",
                      background: "red",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button
        type="submit"
        style={{
          backgroundColor: "#007bff",
          color: "#fff",
          padding: "8px 16px",
          border: "none",
          borderRadius: 5,
          cursor: "pointer",
        }}
      >
        Submit Reply
      </button>
    </form>
  );
};

const NestedReplyBox = ({
  reply,
  post,
  nestedReplyText,
  setNestedReplyText,
  nestedReplyMedia,
  setNestedReplyMedia,
  handleNestedReplySubmit,
  userId,
}) => {
  const mediaList = nestedReplyMedia[reply.id] || [];

  return (
    <div
      style={{
        marginTop: "10px",
        paddingLeft: "40px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      {/* Text input */}
      <MentionInput
        userId={userId}
        placeholder="Write a reply... Type @ to mention a connection"
        value={nestedReplyText[reply.id] || ""}
        onChange={(value) =>
          setNestedReplyText((prev) => ({ ...prev, [reply.id]: value }))
        }
        rows={2}
        style={{
          minHeight: "50px",
          borderRadius: "6px",
          border: "1px solid #ddd",
          padding: "8px",
          resize: "none",
          fontSize: "14px",
        }}
      />

      {/* Media preview */}
      {mediaList.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {mediaList.map((file, idx) => {
            const url = file.url || URL.createObjectURL(file);
            const isImage = /\.(jpg|jpeg|png|gif)$/i.test(url);
            const isVideo = /\.(mp4|webm|ogg)$/i.test(url);

            return (
              <div key={idx} style={{ position: "relative" }}>
                {isImage && (
                  <img
                    src={url}
                    alt="preview"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    }}
                  />
                )}
                {isVideo && (
                  <video
                    src={url}
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    }}
                    controls
                  />
                )}
                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setNestedReplyMedia((prev) => ({
                      ...prev,
                      [reply.id]: prev[reply.id].filter((_, i) => i !== idx),
                    }));
                  }}
                  style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-5px",
                    background: "#d9534f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    lineHeight: "16px",
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Submit button */}
      <button
        onClick={() => handleNestedReplySubmit(post.id, reply.id, post.group_id)}
        style={{
          alignSelf: "flex-end",
          background: "#0077b6",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          padding: "6px 12px",
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        Reply
      </button>
    </div>
  );
};

const NestedReplies = ({
  nestedReplies,
  reply,
  groupId,
  showNestedReplies,
  setShowNestedReplies,
  handleLike,
  handleDeleteReply,
  expandedContent,
  setExpandedContent,
  isImage,
  isVideo,
  getMimeType,
  user,
  members = [],
  groupCreatedBy = null,
  userLiked,
  likes,
  LikeButtons,
  UnLikeButtons,
  DeleteButtons,
  formatDate,
  createLinkifiedText,
}) => {
  const visible = showNestedReplies[reply.id];

  return (
    <div style={{ marginTop: "10px", paddingLeft: "40px" }}>
      {/* Toggle show/hide nested replies */}
      <button
        onClick={() =>
          setShowNestedReplies((prev) => ({
            ...prev,
            [reply.id]: !visible,
          }))
        }
        style={{
          border: "none",
          background: "transparent",
          color: "#0077b6",
          fontSize: "14px",
          cursor: "pointer",
          marginBottom: "6px",
        }}
      >
        {visible
          ? "Hide replies"
          : `View ${nestedReplies.length} repl${
              nestedReplies.length > 1 ? "ies" : "y"
            }`}
      </button>

      {visible && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {nestedReplies.map((nested, idx) => (
            <div
              key={nested.id || idx}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                background: "#fafafa",
                padding: "8px",
                borderRadius: "8px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
              }}
            >
              <img
                src={resolveAvatarUrl(nested, API_URL)}
                alt="avatar"
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                }}
                onError={handleAvatarError}
              />
              <div>
                <strong>
                  {authorDisplayName(nested)}
                </strong>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {formatDate(nested.created_at)}
                </div>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#374151",
                    marginBottom: "6px",
                    whiteSpace: "pre-wrap",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: createLinkifiedText(
                      expandedContent[nested.id]
                        ? String(nested.text || "")
                        : String(nested.text || "")
                            .split(" ")
                            .slice(0, 50)
                            .join(" ") +
                          ((nested.text || "").split(" ").length > 50
                            ? "..."
                            : "")
                    ),
                  }}
                ></p>

                {/* Media preview */}
                {Array.isArray(nested.media) && nested.media.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {nested.media.map((m, i) => {
                      const mime = getMimeType(m.url, m.type);
                      return (
                        <div key={i} style={{ position: "relative" }}>
                          {isImage(m.url, m.type) && (
                            <img
                              src={m.url}
                              alt="img"
                              style={{
                                width: "60px",
                                height: "60px",
                                objectFit: "cover",
                                borderRadius: "6px",
                              }}
                            />
                          )}
                          {isVideo(m.url, m.type) && (
                            <video
                              src={m.url}
                              controls
                              style={{
                                width: "60px",
                                height: "60px",
                                borderRadius: "6px",
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: "flex", gap: "4px", marginTop: "4px" }}>
                  {/* Like */}
                  <button
                    onClick={() => handleLike(nested.id, groupId || reply.group_id, "reply")}
                    style={{
                      fontSize: "12px",
                      color: userLiked[`reply_${nested.id}`]
                        ? "#0077b6"
                        : "#555",
                      fontWeight: "bold",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {userLiked[`reply_${nested.id}`]
                      ? <LikeButtons />
                      : <UnLikeButtons />}
                    <span style={{ color: "#666" }}>
                      {likes[`reply_${nested.id}`] || 0}
                    </span>
                  </button>

                  {/* Delete */}
                  {user &&
                    canDeleteGroupReply(user, nested, members, groupCreatedBy) && (
                      <button
                        onClick={() => handleDeleteReply(nested.id)}
                        style={{
                          fontSize: "12px",
                          color: "#d9534f",
                          fontWeight: "bold",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <DeleteButtons />
                      </button>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GroupPosts = ({ user, onRefresh, refreshKey = 0 }) => {
  const { groupId } = useParams();
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [groupCreatedBy, setGroupCreatedBy] = useState(null);
const [visibleReplyForms, setVisibleReplyForms] = useState({});
  const [expandedContent, setExpandedContent] = useState({});
  const [likes, setLikes] = useState({});
 const [userLiked, setUserLiked] = useState({});
  const [showReplies, setShowReplies] = useState({});
  const [loadingReply, setLoadingReply] = useState(false);
const [popupPhoto, setPopupPhoto] = useState(null);
  const [showFullContent, setShowFullContent] = useState(false);
const [expandedPostId, setExpandedPostId] = useState(null);
  const closePopup = () => setPopupPhoto(null);
const toggleContent = () => setShowFullContent(prev => !prev);
   const [popupImage, setPopupImage] = useState(null);
const [repliesByPostId, setRepliesByPostId] = useState({});
const [nestedReplyMedia, setNestedReplyMedia] = useState({});
  const openImagePopup = (url) => setPopupImage(url);
const [showAllImages, setShowAllImages] = useState({}); // key by reply.id
const [replyContents, setReplyContents] = useState({});
  const [mediaFiles, setMediaFiles] = useState({});
  const [showReplyBox, setShowReplyBox] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
const [replies, setReplies] = useState({});

const [showNestedReplies, setShowNestedReplies] = useState({});
  const [visibleReplies, setVisibleReplies] = useState({});

const [nestedReplyFiles, setNestedReplyFiles] = useState({});
  const scrollRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState({});
  const [editPost, setEditPost] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [whoLiked, setWhoLiked] = useState({});
  const [showLikesModal, setShowLikesModal] = useState(null);
  const [userReactions, setUserReactions] = useState({});
  const [nestedReplyText, setNestedReplyText] = useState({});
  const [pendingNewCount, setPendingNewCount] = useState(0);
  const pendingNewRef = useRef([]);
const Arrow = ({ onClick, direction }) => (
  <div
    onClick={onClick}
    style={{
      position: 'absolute',
      top: '50%',
      [direction]: 10,
      transform: 'translateY(-50%)',
      zIndex: 2,
      backgroundColor: '#fff',
      borderRadius: '50%',
      padding: 8,
      boxShadow: '0 0 8px rgba(0,0,0,0.1)',
      cursor: 'pointer',
    }}
  >
    {direction === 'left' ? <FaChevronLeft size={20} /> : <FaChevronRight size={20} />}
  </div>
);


  const toggleReplies = (postId) => {
  setShowReplies((prev) => ({
    ...prev,
    [postId]: !prev[postId],
  }));
};
  const toggleReplyForm = (postId) => {
  setVisibleReplyForms((prev) => ({
    ...prev,
    [postId]: !prev[postId],
  }));
};
  // ✅ Main fetchPosts function (now exists!)
 const fetchPosts = useCallback(async (options = {}) => {
  const merge = options.merge === true;
  const prependNew =
    options.prependNew === 'if-near-top'
      ? isFeedNearTop()
      : options.prependNew !== false;
  try {
    const res = await fetch(`${API_URL}/get_group_posts.php?group_id=${groupId}&user_id=${user.id}`);
    const data = await res.json();

    if (data.success) {
      const postsWithThreads = (data.posts || []).map((p) => {
        const flat = p.replies || [];
        return {
          ...p,
          replyCount: flat.length,
          replies: nestGroupReplies(flat),
        };
      });

      if (merge) {
        const pendingToFlush = prependNew ? pendingNewRef.current : [];
        if (prependNew) pendingNewRef.current = [];
        let mergeResult = { heldNew: [], prepended: [], changed: false };
        setPosts((prev) => {
          const seen = new Set(prev.map((post) => String(post.id)));
          const extra = pendingToFlush.filter((post) => !seen.has(String(post.id)));
          const base = extra.length ? [...extra, ...prev] : prev;
          mergeResult = mergeFeedPosts(base, postsWithThreads, {
            prependNew,
            removeMissing: true,
          });
          return mergeResult.changed ? mergeResult.posts : base;
        });
        if (mergeResult.heldNew.length) {
          const pendingMap = new Map(
            pendingNewRef.current.map((post) => [String(post.id), post])
          );
          mergeResult.heldNew.forEach((post) => pendingMap.set(String(post.id), post));
          pendingNewRef.current = Array.from(pendingMap.values());
        }
        if (mergeResult.prepended.length) {
          const shown = new Set(mergeResult.prepended.map((post) => String(post.id)));
          pendingNewRef.current = pendingNewRef.current.filter(
            (post) => !shown.has(String(post.id))
          );
        }
        setPendingNewCount(pendingNewRef.current.length);
      } else {
        pendingNewRef.current = [];
        setPendingNewCount(0);
        setPosts(postsWithThreads);
      }

      const likeCounts = {};
      const likedStatus = {};
      const repliesMap = {};

      for (const post of postsWithThreads) {
        const postKey = `post_${post.id}`;
        likeCounts[postKey] = post.like_count || 0;
        likedStatus[postKey] = post.user_liked || false;

        const walkReplies = (nodes) => {
          (nodes || []).forEach((reply) => {
            const replyKey = `reply_${reply.id}`;
            likeCounts[replyKey] = reply.like_count || 0;
            likedStatus[replyKey] = reply.user_liked || false;
            if (reply.children?.length) walkReplies(reply.children);
          });
        };
        walkReplies(post.replies);
        repliesMap[post.id] = post.replies || [];
      }

      setLikes(likeCounts);
      setUserLiked(likedStatus);
      setRepliesByPostId(repliesMap);
      setWhoLiked(data.who_liked || {});
      setUserReactions(data.user_reactions || {});
    }
  } catch (err) {
    console.error('Error fetching posts:', err);
  }
}, [groupId, user?.id]);

const fetchGroupInfo = async () => {
  if (!groupId) return;
  try {
    const res = await fetch(`${API_URL}/get_group_info.php?group_id=${groupId}`);
    const data = await res.json();
    if (data.success && data.group) {
      setMembers(data.group.members || []);
      setGroupCreatedBy(data.group.created_by ?? null);
    }
  } catch (err) {
    console.error('Error fetching group info:', err);
  }
};

useEffect(() => {
  if (groupId && user?.id) {
    fetchPosts();
    fetchGroupInfo();
  }
}, [groupId, user, refreshKey, fetchPosts]);

useLiveRefresh(
  () => fetchPosts({ merge: true, prependNew: 'if-near-top' }),
  10000,
  Boolean(groupId && user?.id)
);

const applyPendingNewPosts = useCallback(() => {
  const toAdd = pendingNewRef.current;
  pendingNewRef.current = [];
  setPendingNewCount(0);
  if (!toAdd.length) return;
  setPosts((prev) => {
    const seen = new Set(prev.map((post) => String(post.id)));
    const unique = toAdd.filter((post) => !seen.has(String(post.id)));
    return unique.length ? [...unique, ...prev] : prev;
  });
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, []);

useEffect(() => {
  if (!pendingNewCount) return undefined;
  const onScroll = () => {
    if (isFeedNearTop()) applyPendingNewPosts();
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => window.removeEventListener('scroll', onScroll);
}, [pendingNewCount, applyPendingNewPosts]);

const [activeReplyBox, setActiveReplyBox] = useState({});

const parseJsonResponse = async (res) => {
  const raw = await res.text();
  try {
    return JSON.parse(raw);
  } catch {
    console.error('Invalid JSON:', raw);
    throw new Error('Server returned an invalid response.');
  }
};

const handleNestedReplySubmit = async (postId, parentId, groupIdForPost) => {
  const text = nestedReplyText?.[parentId]?.trim() || "";
  const mediaList = nestedReplyMedia?.[parentId] || nestedReplyFiles?.[parentId] || [];
  const hasMedia = mediaList.length > 0;

  if (!text && !hasMedia) {
    alert("Please write a reply or attach media.");
    return false;
  }

  const formData = new FormData();
  formData.append("post_id", postId);
  formData.append("group_id", groupIdForPost || groupId);
  formData.append("user_id", user.id);
  appendMentionedField(formData, "content", text || " ");
  formData.append("username", user.username || "");
  formData.append("country", user.country || "");
  if (parentId) {
    formData.append("parent_id", parentId);
  }

  mediaList.forEach((file) => formData.append("media[]", file));

  try {
    const res = await fetch(`${API_URL}/group_post_reply.php`, {
      method: "POST",
      body: formData,
    });

    const result = await parseJsonResponse(res);

    if (result.success) {
      setNestedReplyText((prev) => ({ ...prev, [parentId]: "" }));
      setNestedReplyMedia((prev) => ({ ...prev, [parentId]: [] }));
      setNestedReplyFiles((prev) => ({ ...prev, [parentId]: [] }));
      setActiveReplyBox((prev) => ({ ...prev, [parentId]: false }));
      setShowReplies((prev) => ({ ...prev, [postId]: true }));
      await fetchPosts();
      onRefresh?.();
      return true;
    }
    alert(result.message || "Failed to post reply.");
    return false;
  } catch (err) {
    console.error("Error submitting nested reply:", err);
    alert(err.message || "Network error while posting reply.");
    return false;
  }
};

const handleReplyChange = (postId, value) => {
  setReplyContents((prev) => ({ ...prev, [postId]: value }));
  setErrors((prev) => ({ ...prev, [postId]: '' }));
};


const handleReplySubmit = async (postId) => {
  const replyText = replyContents[postId]?.trim();
  const hasMedia = mediaFiles[postId] && mediaFiles[postId].length > 0;

  // Validate input
  if (!replyText && !hasMedia) {
    setErrors((prev) => ({ ...prev, [postId]: 'Please write a reply or attach media.' }));
    return;
  }

  setLoading((prev) => ({ ...prev, [postId]: true }));

  // Prepare FormData
  const formData = new FormData();
  formData.append('post_id', postId);
  formData.append('user_id', user.id);
  appendMentionedField(formData, 'text', replyText || '');

  if (hasMedia) {
    mediaFiles[postId].forEach((file) => formData.append('media[]', file));
  }

  try {
    // Submit reply
    const res = await fetch(`${API_URL}/add_reply.php`, { method: 'POST', body: formData });
    const rawText = await res.text();
    let data;

    try {
      data = JSON.parse(rawText);
    } catch (parseError) {
      console.error('Invalid JSON:', rawText);
      alert('Server returned invalid response. See console for details.');
      return;
    }

    if (data.success) {
      // Clear input and media
      setReplyContents((prev) => ({ ...prev, [postId]: '' }));
      setMediaFiles((prev) => ({ ...prev, [postId]: [] }));
      setShowReplyBox((prev) => ({ ...prev, [postId]: false }));
      setShowReplies((prev) => ({ ...prev, [postId]: true }));

      // Optionally refresh the entire post list
      onRefresh();

      // Fetch latest replies for this post
      try {
        const resReplies = await fetch(`${API_URL}/get_replies.php?postId=${postId}`);
        const replyData = await resReplies.json();

        if (replyData.success) {
          setReplies((prev) => ({ ...prev, [postId]: replyData.replies }));

          // Smooth scroll to the new reply
          setTimeout(() => {
            scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          console.warn('Failed to refresh replies:', replyData.message);
        }
      } catch (refreshErr) {
        console.error('Error refreshing replies:', refreshErr);
      }
    } else {
      alert(data.message || 'Reply submission failed');
    }
  } catch (err) {
    console.error('Network error:', err);
    alert('Network or server error: ' + err.message);
  } finally {
    setLoading((prev) => ({ ...prev, [postId]: false }));
  }
};


const handleDeleteReply = async (replyId, groupId) => {
  if (window.confirm("Are you sure you want to delete this reply?")) {
    try {
      const formData = new FormData();
      formData.append('reply_id', replyId);
      // New line to send the group_id to the backend
      formData.append('group_id', groupId);
      if (user?.id) formData.append('user_id', user.id);

      const response = await fetch(`${API_URL}/delete_reply_group.php`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        alert(result.message); fetchPosts(); // ✅ now this works!
        onRefresh();
      } else {
        alert("Error: " + result.message);
      }
    } catch (error) {
      console.error("Failed to delete reply:", error);
   
    }
  }
};

  const handleReply = async (postId, replyContent, files, groupIdForPost) => {
    if (!user) {
      alert("Login required to reply.");
      return false;
    }

    const text = (replyContent || "").trim();
    const fileList = Array.isArray(files) ? files : files ? [files] : [];
    if (!text && fileList.length === 0) {
      alert("Please write a reply or attach media.");
      return false;
    }

    try {
      const formData = new FormData();
      formData.append("post_id", postId);
      formData.append("group_id", groupIdForPost || groupId);
      formData.append("user_id", user.id);
      appendMentionedField(formData, "content", text || " ");
      formData.append("username", user.username || "");
      formData.append("country", user.country || "");

      fileList.forEach((file) => formData.append("media[]", file));

      const res = await fetch(`${API_URL}/group_post_reply.php`, {
        method: "POST",
        body: formData,
      });

      const data = await parseJsonResponse(res);
      if (data.success) {
        setShowReplies((prev) => ({ ...prev, [postId]: true }));
        await fetchPosts();
        onRefresh?.();
        return true;
      }
      alert(data.message || "Reply failed.");
      return false;
    } catch (err) {
      console.error("Error posting reply:", err);
      alert(err.message || "Error posting reply.");
      return false;
    }
  };



const formatDate = (timestamp) => {
  const targetTimeZone = "Asia/Manila";
  const date = new Date(timestamp); // let JS parse it

  const now = new Date();
  const diffSeconds = Math.floor((now - date) / 1000);

  if (diffSeconds < 60) return "just now";
  if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  }
  if (diffSeconds < 86400) {
    const hours = Math.floor(diffSeconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: targetTimeZone
  }).format(date);
};



const handleLike = async (id, groupId, type = 'post', reactionType = DEFAULT_REACTION) => {
  if (!user?.id) {
    alert('Login required to like.');
    return;
  }
  const gid = Number(groupId);
  if (!gid) {
    console.error('handleLike: missing group_id', { id, groupId, type });
    return;
  }

  const key = `${type}_${id}`;
  const liked = userLiked[key];
  const currentReaction = userReactions[key] || DEFAULT_REACTION;
  const removing =
    reactionType === null || (liked && reactionType === currentReaction);
  const changing = liked && !removing && reactionType !== currentReaction;
  const url = removing ? 'unlike_group_post.php' : 'like_group_post.php';

  try {
    const body = {
      user_id: user.id,
      target_id: id,
      group_id: gid,
      target_type: type,
    };
    if (!removing) {
      body.reaction_type = reactionType || DEFAULT_REACTION;
    }

    const res = await fetch(`${API_URL}/${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(body),
    });

    const data = await parseJsonResponse(res);
    if (data.success) {
      const nextReaction = removing
        ? null
        : data.reaction_type || reactionType || DEFAULT_REACTION;

      setLikes((prev) => ({
        ...prev,
        [key]: removing
          ? Math.max(0, (prev[key] || 1) - 1)
          : changing
            ? prev[key] || 1
            : (prev[key] || 0) + 1,
      }));
      setUserLiked((prev) => ({
        ...prev,
        [key]: !removing,
      }));
      setUserReactions((prev) => {
        const next = { ...prev };
        if (removing) delete next[key];
        else next[key] = nextReaction;
        return next;
      });
      setWhoLiked((prev) => {
        const currentList = Array.isArray(prev[key]) ? prev[key] : [];
        const entry = {
          user_id: user.id,
          username: user.username,
          first_name: user.first_name || user.firstname || '',
          last_name: user.last_name || user.lastname || '',
          profile_pic: user.profile_pic,
          reaction_type: nextReaction || DEFAULT_REACTION,
        };

        if (removing) {
          return {
            ...prev,
            [key]: currentList.filter((e) => e.user_id !== user.id),
          };
        }

        const idx = currentList.findIndex((e) => e.user_id === user.id);
        if (idx >= 0) {
          const updated = [...currentList];
          updated[idx] = { ...updated[idx], reaction_type: nextReaction };
          return { ...prev, [key]: updated };
        }

        return { ...prev, [key]: [...currentList, entry] };
      });
    } else {
      if (!liked && data.message !== 'Already liked') {
        console.warn(data.message);
        alert(data.message || 'Like failed');
      }
    }
  } catch (err) {
    console.error('Like/unlike request failed:', err.message);
  }
};
const handleEdit = (post) => {
  setEditPost(post);
  setShowEditModal(true);
};

const handleDelete = async (postId, postType) => {
  if (!postId || isNaN(postId)) {
    alert("❌ Invalid post ID.");
    return;
  }

  if (!window.confirm(
    postType === 'hiring'
      ? 'Delete this job posting?'
      : 'Are you sure you want to delete this post?'
  )) return;

  try {
    const formData = new FormData();
    formData.append('post_id', postId);
    if (user?.id) formData.append('user_id', user.id);

    const response = await fetch(`${API_URL}/delete_groupPost.php`, {
      method: 'POST',
      body: formData,
    });

    const text = await response.text();

    let result;
    try {
      result = JSON.parse(text);
    } catch (parseErr) {
      console.error("❌ Response is not valid JSON. Raw response:");
      console.error(text); // likely HTML error
      alert("❌ Server returned an invalid response. Check console for details.");
      return;
    }

    if (result.success) {

      
     alert("Post deleted successfully!");
      fetchPosts(); // ✅ refresh posts after deletion
    } else {
      alert("❌ Delete failed: " + (result.message || "Unknown error."));
    }
  } catch (err) {
    console.error("❌ Error while deleting post:", err);
    alert("❌ A network error occurred. See console for details.");
  }
};

const isImage = (url, type) => isMediaImage(url, type);
const isVideo = (url, type) => isMediaVideo(url, type);
const isAudio = (url) => /\.(mp3|wav|ogg)$/i.test(url);
const isDocument = (url) => /\.(pdf|docx?|pptx?|xlsx?)$/i.test(url);

const getMimeType = (url, type) => getMediaMimeType(url, type);

  const replyCtx = {
    API_URL,
    user,
    userLiked,
    likes,
    expandedContent,
    setExpandedContent,
    showAllImages,
    setShowAllImages,
    activeReplyBox,
    setActiveReplyBox,
    nestedReplyText,
    setNestedReplyText,
    nestedReplyMedia,
    setNestedReplyMedia,
    handleLike,
    handleDeleteReply,
    handleNestedReplySubmit,
    formatDate,
    createLinkifiedText,
    members,
    groupCreatedBy,
  };

  return (
<div className="post-list group-posts-list">
      <NewPostsBanner count={pendingNewCount} onClick={applyPendingNewPosts} />

      {showEditModal && editPost?.post_type === 'hiring' && (
        <HiringForm
          post={editPost}
          user={user}
          groupId={groupId}
          onClose={() => {
            setShowEditModal(false);
            setEditPost(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditPost(null);
            fetchPosts();
            onRefresh?.();
          }}
          onRefresh={() => {
            fetchPosts();
            onRefresh?.();
          }}
        />
      )}
      {showEditModal && editPost?.post_type === 'looking_for_job' && (
        <LookingForJobForm
          post={editPost}
          user={user}
          groupId={groupId}
          onClose={() => {
            setShowEditModal(false);
            setEditPost(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setEditPost(null);
            fetchPosts();
            onRefresh?.();
          }}
          onRefresh={() => {
            fetchPosts();
            onRefresh?.();
          }}
        />
      )}
      {showEditModal && editPost && !['hiring', 'looking_for_job', 'article', 'blog'].includes(editPost.post_type) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => {
            setShowEditModal(false);
            setEditPost(null);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '20px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <EditGroupPostForm
              post={editPost}
              user={user}
              onCancel={() => {
                setShowEditModal(false);
                setEditPost(null);
              }}
              onSuccess={() => {
                setShowEditModal(false);
                setEditPost(null);
                fetchPosts();
                onRefresh?.();
              }}
            />
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="group-empty">
          <div className="group-empty__icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <strong>No posts yet</strong>
          <p>Be the first to share a photo, video, or update with this group.</p>
        </div>
      ) : (
        posts.map((post) => (
         <div
        key={post.id}
        className="post-card"
        style={{ ...getPostCardStyle(), overflow: 'visible' }}
      >

      <div style={{ position: 'relative' }}>
      
      
     {user && (canEditGroupPost(user, post) || canDeleteGroupPost(user, post, members, groupCreatedBy)) && (
        <div className="am-anchor">
          <div className="am-wrap">
            <button
              type="button"
              className="am-trigger"
              aria-label="Post options"
              aria-expanded={Boolean(showDropdown?.[post.id])}
              onClick={() => setShowDropdown((prev) => ({ ...prev, [post.id]: !prev[post.id] }))}
            >
              ⋮
            </button>

            {showDropdown?.[post.id] && (
              <div className="am-menu" role="menu">
                {canEditGroupPost(user, post) && !['article', 'blog'].includes(post.post_type) && (
                  <button
                    type="button"
                    role="menuitem"
                    className="am-item"
                    onClick={() => {
                      handleEdit(post);
                      setShowDropdown((prev) => ({ ...prev, [post.id]: false }));
                    }}
                  >
                    <span className="am-icon am-icon--edit" aria-hidden="true">
                      <Pencil strokeWidth={2.2} />
                    </span>
                    <span className="am-label">Edit</span>
                  </button>
                )}
                {canDeleteGroupPost(user, post, members, groupCreatedBy) && (
                <button
                  type="button"
                  role="menuitem"
                  className="am-item am-item--danger"
                  onClick={() => {
                    handleDelete(post.id, post.post_type);
                    setShowDropdown((prev) => ({ ...prev, [post.id]: false }));
                  }}
                >
                  <span className="am-icon am-icon--delete" aria-hidden="true">
                    <Trash2 strokeWidth={2.2} />
                  </span>
                  <span className="am-label">Delete</span>
                </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
            <PostCardHeader post={post} API_URL={API_URL} formatDate={formatDate} />

        {post.post_type === 'article' ? (
          <ArticleCard post={post} interactive={false} />
        ) : post.post_type === 'blog' ? (
          <ArticleCard post={post} linkPrefix="/home/blog" {...BLOG_CARD_PROPS} />
        ) : post.post_type === 'hiring' ? (
          <HiringCard post={post} interactive={false} />
        ) : post.post_type === 'looking_for_job' ? (
          <LookingForJobCard post={post} interactive={false} />
        ) : (
          <>
            <p
              style={postContentStyle}
              dangerouslySetInnerHTML={{
                __html:
                  expandedContent[post.id] ||
                  String(post.content || "").split(" ").length <= 50
                    ? createLinkifiedText(String(post.content || ""))
                    : createLinkifiedText(
                        String(post.content || "")
                          .split(" ")
                          .slice(0, 50)
                          .join(" ") + "..."
                      ),
              }}
            ></p>

            {String(post.content || "").split(" ").length > 50 && (
              <button
                onClick={() =>
                  setExpandedContent((prev) => ({
                    ...prev,
                    [post.id]: expandedContent[post.id] ? null : post.content,
                  }))
                }
                style={{
                  background: "none",
                  border: "none",
                  color: "#2563eb",
                  cursor: "pointer",
                  fontSize: "14px",
                  padding: 0,
                }}
              >
                {expandedContent[post.id] ? "See less" : "See more"}
              </button>
            )}
            <div>
              {Array.isArray(post.media) && post.media.length > 0 && (
                <PostMediaGrid media={post.media} onImageClick={setPopupPhoto} />
              )}
            </div>
          </>
        )}
      </div>

      <PostActionsSection
        postKey={`post_${post.id}`}
        postId={post.id}
        likeCount={likes[`post_${post.id}`] || 0}
        userLiked={!!userLiked[`post_${post.id}`]}
        userReaction={userReactions[`post_${post.id}`] || null}
        whoLiked={whoLiked}
        replyCount={post.replyCount ?? post.replies?.length ?? 0}
        showReplies={!!showReplies[post.id]}
        onToggleReplies={toggleReplies}
        onLike={() =>
          handleLike(post.id, post.group_id, 'post', DEFAULT_REACTION)
        }
        onReact={(reactionType) =>
          handleLike(post.id, post.group_id, 'post', reactionType)
        }
        onToggleReply={(id) =>
          setShowReplyBox((prev) => ({ ...prev, [id]: !prev[id] }))
        }
        shareUrl={`${window.location.origin}/groups/${groupId}`}
        onShowLikesModal={setShowLikesModal}
      />

{showReplyBox[post.id] && (
  <div
    style={{
      marginTop: '10px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '12px',
      backgroundColor: '#f9f9f9',
    }}
  >
    <ReplyForm
      userId={user?.id}
      context={post.content || post.title || ""}
      onSubmit={async (text, file) => {
        const ok = await handleReply(post.id, text, file, post.group_id);
        if (ok) {
          setShowReplyBox((prev) => ({ ...prev, [post.id]: false }));
        }
      }}
    />
  </div>
)}


{/* Replies List */}
{showReplies[post.id] && (
  <div className="group-reply-list">
    {(post.replies || []).map((reply) => (
      <GroupReplyItem
        key={reply.id}
        reply={reply}
        post={post}
        depth={0}
        ctx={replyCtx}
      />
    ))}
  </div>
)}

      </div>
        ))
      )}

      <PhotoLightbox src={popupPhoto} onClose={closePopup} />
      <WhoLikedModal
        showKey={showLikesModal}
        whoLiked={whoLiked}
        onClose={() => setShowLikesModal(null)}
      />
    </div>
  );
};

export default GroupPosts;
